'use client';

import {
  useCallback,
  useEffect,
  useState
} from 'react';

import type {
  ReactNode
} from 'react';

import
  useEmblaCarousel
from 'embla-carousel-react';

import
  Autoplay
from 'embla-carousel-autoplay';

import
  HeroSlide
from '@/app/ui/hero/hero-slide';

import
  GradientOverlay
from '@/app/ui/gradient-overlay';

import {
  heroSlides as slides
} from '@/app/data/hero';

// `announcement` is server-rendered by the page and slotted in here, so a
// time-limited notice (e.g. Revision Week) sits on the first screen, just
// under the floating header, without this client carousel deciding when.
export default function HeroSection({
  announcement
}: { announcement?: ReactNode }) {

  const [ current, setCurrent ] = useState(0);

  const [ emblaRef, emblaApi ] = useEmblaCarousel(
    {
      loop: true,
      align: 'center'
    },
    [ Autoplay({
      delay: 5000,
      stopOnInteraction: false,
      stopOnMouseEnter: true
    }) ]
  );

  const onSelect = useCallback(() => {

    if (!emblaApi) {

      return;
    }

    setCurrent(emblaApi.selectedScrollSnap());
  }, [ emblaApi ]);

  useEffect(() => {

    if (!emblaApi) {

      return;
    }

    emblaApi.on('select', onSelect);

    return () => {

      emblaApi.off('select', onSelect);
    };
  }, [ emblaApi, onSelect ]);

  const goTo = useCallback((i: number) => {

    if (!emblaApi) {

      return;
    }

    emblaApi.scrollTo(i);
  }, [ emblaApi ]);

  return (
    <section className="relative w-full overflow-hidden">

      <video
        className="absolute inset-0 w-full h-full object-cover"
        autoPlay
        muted
        loop
        playsInline
        poster="/hero-poster.webp"
      >
        <source src="/hero-bg.mp4" type="video/mp4" />
      </video>

      <GradientOverlay />

      {announcement && (
        <div className="absolute inset-x-0 top-[calc(5.75rem+env(safe-area-inset-top))] z-20 flex justify-center px-4">
          {announcement}
        </div>
      )}

      <div ref={emblaRef} className="relative z-10 overflow-hidden">
        <div className="flex">
          {slides.map((slide, i) => (
            <div
              key={i}
              className="flex-none w-full min-h-[100svh] flex flex-col items-center justify-center text-center px-6 sm:px-8 pt-24 pb-16"
            >
              <div className="max-w-4xl mx-auto w-full">
                <HeroSlide {...slide} />
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="absolute bottom-10 left-1/2 -translate-x-1/2 z-20 flex items-center">
        {slides.map((_, i) => (
          <button
            key={i}
            onClick={() => goTo(i)}
            aria-label={`Go to slide ${ i + 1 }`}
            className="flex items-center justify-center"
            style={{
              width: '1.75rem',
              height: '1.5rem'
            }}
          >
            <span
              className="rounded-full transition-all duration-300"
              style={{
                display: 'block',
                width: i === current ? '1.75rem' : '0.45rem',
                height: '0.45rem',
                backgroundColor: 'white',
                opacity: i === current ? 1 : 0.35
              }}
            />
          </button>
        ))}
      </div>
    </section>
  );
}
